import { Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import WorkflowRunsPage, {
  type WorkflowRunsColumn,
} from "src/views/products/workflow/pages/WorkflowRunsPage";
import WorkflowRunsEmptyState from "src/views/products/workflow/components/WorkflowRunsEmptyState";
import { PROPOSAL_GENERATION_PRODUCT } from "./proposalGenerationProduct";
import {
  useGetProposalGenerationRuns,
  type RunListItem,
} from "./services/ProposalGenerationServices";

const STAGE_STATUS_COLOR: Record<
  string,
  "default" | "primary" | "success" | "error"
> = {
  pending: "default",
  in_progress: "primary",
  completed: "success",
  failed: "error",
};

/**
 * A failure outranks progress: a run whose stage failed reads as "2 of 3
 * stages" otherwise, which looks like work still underway.
 */
export const deriveRunStatus = (run: RunListItem) => {
  if (run.stages.length === 0) {
    return { label: "empty", color: "default" as const };
  }
  const completed = run.stages.filter((s) => s.status === "completed").length;
  const failed = run.stages.filter((s) => s.status === "failed").length;
  if (failed > 0) {
    return {
      label: failed === 1 ? "1 stage failed" : `${failed} stages failed`,
      color: "error" as const,
    };
  }
  if (completed === run.stages.length) {
    return { label: "completed", color: "success" as const };
  }
  if (run.stages.some((s) => s.status !== "pending")) {
    return {
      label: `${completed} of ${run.stages.length} stages`,
      color: "primary" as const,
    };
  }
  return { label: "pending", color: "default" as const };
};

/** PG's runs dashboard on the shared workflow runs frame. */
const ProposalGenerationRunsPage = () => {
  const history = useHistory();
  const {
    data: runs = [],
    isFetching,
    isError,
    refetch,
  } = useGetProposalGenerationRuns();

  const columns = useMemo<WorkflowRunsColumn<RunListItem>[]>(
    () => [
      { key: "id", header: "#", render: (run) => run.run_id },
      {
        key: "template",
        header: "Template",
        render: (run) => (
          <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>
            {run.template_name ?? "—"}
          </Typography>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (run) => {
          const status = deriveRunStatus(run);
          return (
            <Chip
              label={status.label}
              size="small"
              color={status.color}
              variant="outlined"
            />
          );
        },
      },
      {
        key: "stages",
        header: "Stages",
        render: (run) => (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {run.stages.map((stage) => (
              <Tooltip
                key={stage.dialogue_id}
                title={`${stage.report_type ?? "stage"} — ${stage.status.replace(/_/g, " ")}`}
              >
                <Chip
                  label={stage.sequence_number ?? "?"}
                  size="small"
                  color={STAGE_STATUS_COLOR[stage.status] ?? "default"}
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(
                      `/proposal-generation/chat/${run.run_id}/${stage.dialogue_id}`,
                    );
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        ),
      },
      {
        key: "created_by",
        header: "Created by",
        render: (run) => (
          <Typography variant="caption" color="text.secondary">
            {run.created_by ?? "—"}
          </Typography>
        ),
      },
      {
        key: "created",
        header: "Created",
        render: (run) => (
          <Typography variant="caption" color="text.secondary">
            {run.create_date
              ? new Date(run.create_date).toLocaleDateString()
              : "—"}
          </Typography>
        ),
      },
      {
        key: "actions",
        header: "",
        align: "right",
        render: (run) =>
          run.review_dialogue_id ? (
            <Button
              size="small"
              startIcon={<ForumOutlinedIcon />}
              onClick={(e) => {
                e.stopPropagation();
                history.push(
                  `/proposal-generation/chat/${run.run_id}/${run.review_dialogue_id}`,
                );
              }}
              data-testid={`propgen-run-open-${run.run_id}`}
            >
              Open
            </Button>
          ) : null,
      },
    ],
    [history],
  );

  return (
    <WorkflowRunsPage<RunListItem>
      title="Proposal Generation Runs"
      testIdPrefix={PROPOSAL_GENERATION_PRODUCT.testIdPrefix}
      filters={[]}
      columns={columns}
      rows={runs}
      rowKey={(run) => run.run_id}
      isLoading={isFetching}
      isError={isError}
      onRefresh={() => refetch()}
      onRowClick={(run) =>
        run.review_dialogue_id &&
        history.push(
          `/proposal-generation/chat/${run.run_id}/${run.review_dialogue_id}`,
        )
      }
      emptyState={
        <>
          <WorkflowRunsEmptyState
            message="No runs yet. Start one from the"
            linkHref={PROPOSAL_GENERATION_PRODUCT.basePath}
            linkLabel="Proposal Generation page"
            onNavigate={() => history.push(PROPOSAL_GENERATION_PRODUCT.basePath)}
          />
          .
        </>
      }
    />
  );
};

export default ProposalGenerationRunsPage;
